import React from 'react';
import {
  Box,
  Button,
  InputLabel,
  Popover,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';
import { useField } from 'formik';
import { cx } from '../../helpers/global';

interface Props {
  name: string;
  label: string;
  disabled: boolean;
  hidden: boolean;
  minDate?: Date;
}

const DEFAULT_VALUE = [null, null] as [null, null];
const PLACEHOLDER_DATE = 'MM/DD/YYYY';

const BUTTON_SX: SxProps<Theme> = {
  p: 1,
  fontSize: theme => theme.typography.body2.fontSize,
};

function DateRangeInput({ name, label, disabled, hidden, minDate }: Props) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);

  const [{ value }, { error, touched }, { setTouched, setValue }] =
    useField(name);
  const isError = touched && !!error;
  const [tempValue, setTempValue] = React.useState(
    (value ?? DEFAULT_VALUE) as [Date | null, Date | null],
  );

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setTempValue((value ?? DEFAULT_VALUE) as [Date, Date]);
  };

  const onClose = () => {
    setAnchorEl(null);
    setTouched(true);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  const dates = (value ?? DEFAULT_VALUE) as [Date, Date];

  const onChange = (newDates: [Date, Date]) => {
    setTempValue(newDates.map(d => (d ? new Date(d) : null)) as [Date, Date]);
  };

  const onClear = () => {
    setTempValue(DEFAULT_VALUE);
  };

  const onApply = () => {
    setValue(
      tempValue.map(d => (d ? new Date(d) : null)) as [Date, Date],
      true,
    );
    // Fixes validation error when closing popover
    setTimeout(() => onClose(), 0);
  };

  return (
    <Box
      className={cx('input', hidden && 'hidden', isError && 'error')}
      position="relative">
      <InputLabel htmlFor={name}>
        <Typography variant="h5">{label}</Typography>
      </InputLabel>
      <input
        id={name}
        name={name}
        onClick={onClick}
        value={`${
          dates?.[0] ? format(dates?.[0], 'MM/dd/yyyy') : PLACEHOLDER_DATE
        } - ${
          dates?.[1] ? format(dates?.[1], 'MM/dd/yyyy') : PLACEHOLDER_DATE
        }`}
        disabled={disabled}
        readOnly
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <DateRangeCalendar
          calendars={1}
          value={tempValue}
          onChange={onChange}
          minDate={minDate}
        />
        <Box
          p={2}
          pt={0}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}>
          <Button
            onClick={onClear}
            variant="light"
            color="info"
            type="button"
            sx={BUTTON_SX}>
            Clear
          </Button>
          <Button
            disabled={!tempValue?.[0] || !tempValue?.[1]}
            onClick={onApply}
            variant="base"
            color="primary"
            type="button"
            sx={BUTTON_SX}>
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}

export default DateRangeInput;
